/**
 * TagPicker Component (Story 3.6)
 *
 * A popover for assigning/removing tags from a client.
 * Shows all tenant-level tags with search/filter, checkmarks for assigned tags,
 * and "Create New Tag" inline form.
 *
 * Tag mutations are immediate (not tied to form dirty state).
 */

import { useState, useRef, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Input } from "@provenleap/ui";
import { Plus, Check, MagnifyingGlass, Tag } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";
import { apiGet, apiPost, apiDelete } from "../../lib/apiClient";
import { TAG_COLORS } from "./types";
import type { ClientTag, TenantTag } from "./types";

interface TagPickerProps {
  clientId: string;
  currentTags: ClientTag[];
  onTagAssigned: (tag: ClientTag) => void;
  onTagRemoved: (tagId: string) => void;
  onToast: (type: "success" | "error" | "info", message: string) => void;
}

export function TagPicker({
  clientId,
  currentTags,
  onTagAssigned,
  onTagRemoved,
  onToast,
}: TagPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState<string>(TAG_COLORS[0]!.value);
  const popoverRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const assignedIds = new Set(currentTags.map((t) => t.id));
  const isAtLimit = currentTags.length >= 20;

  // Close popover on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setShowCreateForm(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Fetch all tenant tags
  const { data: allTags = [] } = useQuery<TenantTag[]>({
    queryKey: ["tags"],
    queryFn: async () => {
      const res = await apiGet("/api/tags");
      if (!res.ok) throw new Error("Failed to load tags");
      const json = await res.json();
      return json.data;
    },
    enabled: isOpen,
  });

  const filteredTags = useMemo(
    () =>
      allTags
        .filter((tag) => tag.name.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allTags, search]
  );

  // Assign tag mutation
  const assignTag = useMutation({
    mutationFn: async (tagId: string) => {
      const res = await apiPost(`/api/clients/${clientId}/tags`, { tagId });
      if (!res.ok) {
        const err = await res.json();
        throw { status: res.status, ...err };
      }
      return res.json();
    },
    onSuccess: (_data, tagId) => {
      const tag = allTags.find((t) => t.id === tagId);
      if (tag) {
        onTagAssigned({ id: tag.id, name: tag.name, color: tag.color });
      }
      onToast("success", "Tag added");
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["tags"] });
    },
    onError: (err: any) => {
      if (err?.type?.includes?.("already-assigned")) {
        onToast("info", "Tag is already assigned");
      } else if (err?.type?.includes?.("tag-limit")) {
        onToast("error", "Tag limit reached (20 max)");
      } else {
        onToast("error", err?.title ?? "Failed to add tag");
      }
    },
  });

  // Remove tag mutation
  const removeTag = useMutation({
    mutationFn: async (tagId: string) => {
      const res = await apiDelete(`/api/clients/${clientId}/tags/${tagId}`);
      if (!res.ok) {
        const err = await res.json();
        throw { status: res.status, ...err };
      }
    },
    onSuccess: (_data, tagId) => {
      onTagRemoved(tagId);
      onToast("success", "Tag removed");
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["tags"] });
    },
    onError: (err: any) => {
      onToast("error", err?.title ?? "Failed to remove tag");
    },
  });

  // Create tag mutation
  const createTag = useMutation({
    mutationFn: async ({ name, color }: { name: string; color: string }) => {
      const res = await apiPost("/api/tags", { name, color });
      if (!res.ok) {
        const err = await res.json();
        throw { status: res.status, ...err };
      }
      const json = await res.json();
      return json.data;
    },
    onSuccess: async (newTag) => {
      // Assign the new tag to the client immediately
      try {
        const assignRes = await apiPost(`/api/clients/${clientId}/tags`, { tagId: newTag.id });
        if (assignRes.ok) {
          onTagAssigned({ id: newTag.id, name: newTag.name, color: newTag.color });
          onToast("success", "Tag added");
        } else {
          onToast("error", "Tag created but failed to assign to client");
        }
      } catch {
        onToast("error", "Tag created but failed to assign to client");
      }
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      setNewTagName("");
      setNewTagColor(TAG_COLORS[0]!.value);
      setShowCreateForm(false);
    },
    onError: (err: any) => {
      if (err?.type?.includes?.("duplicate-tag")) {
        onToast("error", "A tag with this name already exists");
      } else {
        onToast("error", err?.title ?? "Failed to create tag");
      }
    },
  });

  function handleTagClick(tag: TenantTag) {
    if (assignedIds.has(tag.id)) {
      removeTag.mutate(tag.id);
    } else if (!isAtLimit) {
      assignTag.mutate(tag.id);
    }
  }

  function handleCreateTag() {
    const trimmed = newTagName.trim();
    if (!trimmed) return;
    createTag.mutate({ name: trimmed, color: newTagColor });
  }

  return (
    <div className="relative" ref={popoverRef}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="cursor-pointer rounded-lg border-[0.5px] shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="tag-picker-trigger"
      >
        <Tag size={14} className="mr-1" />
        Add Tag
      </Button>

      <AnimatePresence>
        {isOpen && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 4 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          className="absolute left-0 bottom-full mb-1 z-[60] w-72 rounded-xl bg-popover shadow-md overflow-hidden"
          style={{ border: "0.5px solid var(--hairline)" }}
          data-testid="tag-picker-popover"
        >
          {/* Search input */}
          <div className="p-2" style={{ borderBottom: "0.5px solid hsl(var(--input) / 0.4)" }}>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search tags..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-7 h-8 text-sm border-0 shadow-none focus-visible:ring-0"
                data-testid="tag-search-input"
              />
            </div>
          </div>

          {/* Tag list */}
          <div className="max-h-48 overflow-y-auto p-2">
            {filteredTags.length === 0 && !showCreateForm && (
              <p className="text-sm text-muted-foreground text-center py-3">
                No tags found
              </p>
            )}
            {filteredTags.map((tag) => {
              const isAssigned = assignedIds.has(tag.id);
              const isDisabled = !isAssigned && isAtLimit;
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => handleTagClick(tag)}
                  disabled={isDisabled}
                  className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] hover:bg-accent ${
                    isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                  }`}
                  title={isDisabled ? "Tag limit reached (20 max)" : undefined}
                  data-testid="tag-picker-item"
                >
                  <span
                    className="h-3 w-3 rounded-full shrink-0"
                    style={{ backgroundColor: tag.color }}
                  />
                  <span className="flex-1 text-left truncate">{tag.name}</span>
                  {isAssigned && (
                    <Check size={14} className="text-primary shrink-0" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Create new tag */}
          <div className="p-2" style={{ borderTop: "0.5px solid hsl(var(--input) / 0.4)" }}>
            {!showCreateForm ? (
              <button
                type="button"
                onClick={() => setShowCreateForm(true)}
                className="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] hover:bg-accent cursor-pointer"
                data-testid="create-tag-button"
              >
                <Plus size={14} />
                Create New Tag
              </button>
            ) : (
              <div className="space-y-2">
                <Input
                  placeholder="Tag name"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  maxLength={50}
                  className="h-8 text-sm"
                  data-testid="new-tag-name-input"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleCreateTag();
                    if (e.key === "Escape") setShowCreateForm(false);
                  }}
                />
                <div className="grid grid-cols-6 gap-1" data-testid="color-palette">
                  {TAG_COLORS.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setNewTagColor(c.value)}
                      className={`h-7 w-7 rounded-full border-2 ${
                        newTagColor === c.value
                          ? "border-foreground"
                          : "border-transparent"
                      }`}
                      style={{ backgroundColor: c.value, minWidth: 0, minHeight: 0 }}
                      title={c.label}
                      aria-label={`Select ${c.label} color`}
                    />
                  ))}
                </div>
                <div className="flex gap-1.5 justify-center">
                  <Button
                    type="button"
                    size="sm"
                    className="h-9 text-xs rounded-xl cursor-pointer"
                    onClick={handleCreateTag}
                    disabled={!newTagName.trim() || createTag.isPending}
                    data-testid="confirm-create-tag"
                  >
                    {createTag.isPending ? "Creating..." : "Create"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-9 text-xs rounded-xl cursor-pointer"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewTagName("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
}
